import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import WhatsAppLoginDialog from "../components/whatsapp/WhatsAppLoginDialog";
import { SectionHeader, LoadError } from "./Settings";
import { QUEUE_STATUS_LABELS, isWhatsAppLinked, whatsappStatusLabel } from "../lib/whatsapp";
import { toast } from "sonner";
import { Loader2, MessageCircle, QrCode, RefreshCw, Unlink } from "lucide-react";

function formatCheckedAt(iso) {
  if (!iso) return "Jamais";
  return new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function formatAge(iso) {
  if (!iso) return null;
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.round(hours / 24)} j`;
}

export default function WhatsApp({ embedded = false }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/status");
      setStatus(data);
      setLoadFailed(false);
    } catch (e) {
      toast.error(errorMessage(e));
      // Distinct from "enabled: false" (a valid, successful response): a failed
      // fetch must never render as if an admin had deliberately disabled the
      // channel.
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const testSend = async () => {
    setTesting(true);
    try {
      await api.post("/whatsapp/test-send");
      toast.success("Message de test envoyé. Vérifiez votre WhatsApp.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  const logout = async () => {
    setLoggingOut(true);
    try {
      await api.post("/whatsapp/logout");
      toast.success("Compte WhatsApp déconnecté. Vous pouvez en connecter un autre.");
      setLogoutOpen(false);
      await loadStatus();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoggingOut(false);
    }
  };

  const onLinked = useCallback((fresh) => {
    setStatus(fresh);
    setLoginOpen(false);
    toast.success(fresh.phone ? `Compte WhatsApp ${fresh.phone} connecté` : "Compte WhatsApp connecté");
  }, []);

  const { label, dot } = whatsappStatusLabel(status);
  const linked = isWhatsAppLinked(status);
  // Account management needs GOWA to answer: never offered while it is unreachable or the channel is off.
  const canManageAccount = !!status?.enabled && !status?.error;
  // Seeing this page (whatsapp:view) is not changing or using the account: that is whatsapp:manage (admin always).
  const mayManage = hasPermission(user, "whatsapp:manage");
  const accountLabel = linked ? status?.phone || status?.device_id || "Compte connecté" : "Aucun compte connecté";
  const hasNumber = !!user?.whatsapp_number;
  const canTest = !!status?.enabled && hasNumber;

  if (loadFailed && !status) {
    return (
      <div className="space-y-4" data-testid="whatsapp-page">
        {embedded ? (
          <SectionHeader title="WhatsApp" description="État de la connexion et supervision des notifications WhatsApp." />
        ) : (
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">WhatsApp</h1>
            <p className="text-sm text-slate-500">État de la connexion WhatsApp utilisée pour les notifications.</p>
          </div>
        )}
        <LoadError onRetry={loadStatus} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="whatsapp-page">
      {embedded ? (
        <SectionHeader title="WhatsApp" description="État de la connexion et supervision des notifications WhatsApp." />
      ) : (
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">WhatsApp</h1>
          <p className="text-sm text-slate-500">État de la connexion WhatsApp utilisée pour les notifications.</p>
        </div>
      )}

      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-jamora/10 text-jamora flex items-center justify-center flex-shrink-0">
            <MessageCircle size={16} />
          </div>
          <div className="font-display font-semibold text-sm text-slate-900">Notifications WhatsApp</div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
            <Loader2 className="animate-spin" size={14} /> Vérification en cours…
          </div>
        ) : (
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <dt className="text-xs text-slate-500 mb-1">État</dt>
              <dd className="flex items-center gap-2 text-slate-900 font-medium">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
                {label}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 mb-1">Compte</dt>
              <dd className="text-slate-900 font-medium truncate" data-testid="whatsapp-account">
                {canManageAccount ? accountLabel : "Visible avec le droit de gestion"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 mb-1">Provider</dt>
              <dd className="text-slate-900 font-medium truncate">{status?.provider ? status.provider.toUpperCase() : "Non configuré"}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 mb-1">Dernière vérif.</dt>
              <dd className="text-slate-900 font-medium">{formatCheckedAt(status?.checked_at)}</dd>
            </div>
          </dl>
        )}

        <div className="pt-2 flex flex-wrap items-center gap-2">
          {!loading && canManageAccount && mayManage && (linked ? (
            <Button variant="outline" size="sm" onClick={() => setLogoutOpen(true)} data-testid="whatsapp-logout">
              <Unlink size={13} className="mr-1.5" />
              Déconnecter ce compte
            </Button>
          ) : (
            <Button size="sm" onClick={() => setLoginOpen(true)} data-testid="whatsapp-login">
              <QrCode size={13} className="mr-1.5" />
              Connecter un compte
            </Button>
          ))}
          {mayManage && (
            <Button variant="outline" size="sm" onClick={testSend} disabled={testing || loading || !canTest || !linked}>
              {testing ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Tester l'envoi
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={loadStatus} disabled={loading}>
            <RefreshCw size={13} className="mr-1.5" />
            Rafraîchir
          </Button>
        </div>
        {!loading && status && !status.enabled ? (
          <p className="text-xs text-slate-500">
            Activez les notifications WhatsApp dans{" "}
            <Link to="/settings/notifications" className="text-jamora hover:underline">Notifications</Link> pour tester l'envoi.
          </p>
        ) : !loading && canManageAccount && !linked ? (
          <p className="text-xs text-slate-500">
            Aucune notification WhatsApp ne part tant qu'aucun compte n'est connecté.
          </p>
        ) : !loading && status?.enabled && !hasNumber ? (
          <p className="text-xs text-slate-500">
            Ajoutez d'abord votre numéro WhatsApp dans votre{" "}
            <Link to="/profile" className="text-jamora hover:underline">profil</Link>.
          </p>
        ) : (
          <p className="text-xs text-slate-500">
            Le test envoie un message à votre propre numéro WhatsApp, renseigné dans votre profil.
          </p>
        )}
      </Card>

      <WhatsAppLoginDialog open={loginOpen} onOpenChange={setLoginOpen} onLinked={onLinked} />
      <AlertDialog open={logoutOpen} onOpenChange={(value) => !loggingOut && setLogoutOpen(value)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Déconnecter le compte WhatsApp{status?.phone ? ` ${status.phone}` : ""} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Plus aucune notification WhatsApp ne partira tant qu'un autre compte n'aura pas été connecté en
              scannant un QR code. Le CRM disparaîtra des appareils connectés du téléphone concerné.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loggingOut}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); logout(); }}
              disabled={loggingOut}
              className="bg-red-600 hover:bg-red-700"
              data-testid="whatsapp-logout-confirm"
            >
              {loggingOut ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Déconnecter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!loading && status?.queue && (
        <Card className="p-5 space-y-4">
          <div className="font-display font-semibold text-sm text-slate-900">File d'envoi</div>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(QUEUE_STATUS_LABELS).map(([key, label]) => (
              <div key={key} className="text-center">
                <div className="text-lg font-semibold text-slate-900">{status.queue.counts?.[key] ?? 0}</div>
                <div className="text-xs text-slate-500">{label}</div>
              </div>
            ))}
          </div>
          <dl className="space-y-2.5 text-sm pt-1 border-t border-slate-100">
            <div className="flex items-center justify-between gap-3 pt-2.5">
              <dt className="text-slate-500">Plus ancien en attente</dt>
              <dd className="text-slate-900 font-medium">{formatAge(status.queue.oldest_pending_created_at) || "Aucun"}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-slate-500">Échecs sur la dernière heure</dt>
              <dd className="text-slate-900 font-medium">{status.queue.recent_failures_1h ?? 0}</dd>
            </div>
          </dl>
          {status.queue.last_failure && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <span className="font-medium">Dernier échec ({status.queue.last_failure.event_type})</span> : {status.queue.last_failure.last_error}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
